import {useCallback, useEffect, useState} from "react";
import {useInfiniteQuery} from "react-query";

import {
  SearchRequest,
  SearchResponse,
  WorkflowCard,
  WorkflowStatus,
  WorkflowsService,
} from "../../../services/WorkflowsService";

export const WORKFLOWS_QUERY_KEY = "workflows";

/** Matches the server default: the most recent N of the status, per page. */
export const DEFAULT_PAGE_SIZE = 10;

/** Shortest term the service filters on. Below it, no term is sent at all. */
export const MIN_SEARCH_TERM_LENGTH = 3;

/** Longest term the contract accepts. Anything above is a 422. */
export const MAX_SEARCH_TERM_LENGTH = 100;

export const normalizeSearchTerm = (query?: string | null) => {
  const trimmed = query?.trim();
  return trimmed && trimmed.length >= MIN_SEARCH_TERM_LENGTH ? trimmed : undefined;
};

type Params = {
  /** Omit for every status — list view's "All", and its default. */
  status?: WorkflowStatus | null;
  facilityIds: ReadonlyArray<string> | null;
  /** Narrows to the caller's stars. Resolved in `reside`, not the replica. */
  starredOnly?: boolean;
  /** Dashboard search term, matched against resident name and workflow code. */
  query?: string | null;
  workflowType?: string | null;
  pageSize?: number;
  sortBy?: SearchRequest["sortBy"];
  sortDirection?: SearchRequest["sortDirection"];
  /** A newly created user without a facility is not allowed to see workflows. */
  isAllowedToSeeAdmissions?: boolean;
};

const toRequest = (
  {
    status,
    facilityIds,
    query,
    workflowType,
    starredOnly,
    pageSize = DEFAULT_PAGE_SIZE,
    sortBy,
    sortDirection,
  }: Params,
  page: number,
): SearchRequest => ({
  // Omitted when absent: to the service, no statuses means every status.
  ...(status ? {statuses: [status]} : {}),
  // Omitted rather than sent empty: the server treats absent as "everything in scope", while
  // an empty list would be a filter matching nothing.
  ...(facilityIds?.length ? {facilityIds: [...facilityIds]} : {}),
  ...(query ? {query} : {}),
  ...(workflowType ? {workflowType} : {}),
  // Always sent, like pageSize: the contract gives it a default, which makes it a required
  // field of the generated request type rather than an optional one.
  starredOnly: Boolean(starredOnly),
  ...(sortBy ? {sortBy} : {}),
  ...(sortDirection ? {sortDirection} : {}),
  page,
  pageSize,
});

/**
 * Patches one card (by id) inside every page of a cached workflows result.
 *
 * The board writes through Java and reads from the replica, so a mutation's response cannot
 * simply be refetched: the replica is a second or two behind, and a refetch inside that window
 * returns the row as it was and visibly undoes what the user just did. Patching the cache is
 * what keeps a checklist tick or a PCC upload on screen until replication catches up.
 *
 * Only the keys the card already carries are taken from the response, and `answers` is left
 * alone: the service narrows it to a preview subset deliberately, and overlaying the full set
 * from the Java DTO would put tens of kilobytes of unread answers into every patched card.
 *
 * Returns the original reference when the card is not in this query, so unrelated columns do
 * not re-render.
 */
export const replaceCardInWorkflowsData = (
  data: {pages: SearchResponse[]} | undefined,
  admission: {id: string} & Record<string, unknown>,
): {pages: SearchResponse[]} | undefined => {
  if (!data?.pages?.length) {
    return data;
  }

  const merge = (card: WorkflowCard): WorkflowCard => {
    const patched: Record<string, unknown> = {...card};
    for (const key of Object.keys(card)) {
      if (key === "answers" || !(key in admission)) {
        continue;
      }
      patched[key] = admission[key];
    }
    // Cards carry the count, Java returns the flags themselves; same rule as getFlagCount.
    if ("flagsCount" in card && Array.isArray(admission.flags)) {
      patched.flagsCount = admission.flags.length;
    }
    return patched as WorkflowCard;
  };

  let changed = false;
  const pages = data.pages.map(page => {
    if (!page?.cards?.some(card => card.id === admission.id)) {
      return page;
    }
    changed = true;
    return {
      ...page,
      cards: page.cards.map(card => (card.id === admission.id ? merge(card) : card)),
    };
  });

  return changed ? {...data, pages} : data;
};

/**
 * Workflows the user has just moved off the board, held back until the replica agrees.
 *
 * Suppression rather than a cache edit. The board writes through Java and reads the replica, so
 * for a second or two *any* fetch returns the pre-mutation row -- not only the one this mutation
 * triggered. Editing the cache after that one refetch left the next one free to write the card
 * straight back: a second approve, or the refetch that starring fires, would undo it. Filtering
 * on the way out covers every path, because none of them can reach the component without it.
 *
 * Module-level, so one column's suppression is visible to the others: the same workflow appears
 * in the list view's "All" as well as its own grid column.
 */
const suppressed = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach(listener => listener());

export const suppressCard = (admissionId: string) => {
  suppressed.add(admissionId);
  notify();
};

export const restoreCard = (admissionId: string) => {
  if (suppressed.delete(admissionId)) {
    notify();
  }
};

/** For tests: nothing carried between cases. */
export const clearSuppressedCards = () => {
  suppressed.clear();
  notify();
};

/**
 * Re-runs `select` when the suppressed set changes.
 *
 * react-query only re-runs select when the data or the select function changes, and a refetch
 * that returns the same rows leaves the data reference alone. Without this, restoring a card
 * would not reach the screen until something else moved -- a workflow hidden indefinitely,
 * which is a worse failure than one that reappears.
 */
const useSuppressionRevision = () => {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const listener = () => setRevision(current => current + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return revision;
};

const stripSuppressed = (data: {
  pages: SearchResponse[];
  pageParams: unknown[];
}) => {
  if (!suppressed.size) {
    return data;
  }

  let removed = 0;
  const pages = data.pages.map(page => {
    const cards = page.cards.filter(card => !suppressed.has(card.id));
    if (cards.length === page.cards.length) {
      return page;
    }
    removed += page.cards.length - cards.length;
    return {...page, cards};
  });

  if (!removed) {
    return data;
  }

  // Every page carries the same total, so the header cannot disagree with the list under it.
  return {
    ...data,
    pages: pages.map(page => ({
      ...page,
      total: Math.max(0, page.total - removed),
    })),
  };
};

/**
 * Workflows the user has just created or saved, shown until the replica returns them.
 *
 * The inverse of suppression, for the same reason. Create and save go through Java, and the board
 * reads the replica, which trails by up to a transform poll (5s): coming back to the dashboard
 * straight after saving a pre-flight fetched a board without it, and nothing fetched again. The
 * response to the write already is the workflow, so it is held here and merged in on the way out,
 * where no refetch can undo it, until the server returns a row at least as new.
 *
 * Module-level and shared with suppression's listeners, so every column sees it at once.
 *
 * Held until the server returns it, not for a fixed time. Nothing refetches the board after a
 * create or save, so a card dropped on a timer fell back to a cache from before it existed: save
 * one pre-flight, then another more than 30s later, and the first vanished until a reload. Each
 * column instead refetches once the replica should have caught up (see `useWorkflowsQuery`),
 * and the TTL is only a backstop for a card the server never returns, e.g. deleted elsewhere.
 */
const RECENTLY_WRITTEN_TTL_MS = 10 * 60_000;

/** How long after a write the replica is expected to have it: a transform poll (5s), with room. */
export const REPLICA_CATCH_UP_MS = 8_000;

const recentlyWritten = new Map<
  string,
  {card: WorkflowCard; writtenAt: number}
>();

export const rememberWrittenCard = (card: WorkflowCard) => {
  const now = Date.now();
  recentlyWritten.forEach(({writtenAt}, id) => {
    if (now - writtenAt > RECENTLY_WRITTEN_TTL_MS) {
      recentlyWritten.delete(id);
    }
  });
  recentlyWritten.set(card.id, {card, writtenAt: now});
  notify();
};

/**
 * When the replica should have the latest remembered write, or null when nothing is remembered.
 * A column whose data is older than this refetches at that moment.
 */
const replicaCatchUpAt = (now: number): number | null => {
  let latest: number | null = null;
  recentlyWritten.forEach(({writtenAt}) => {
    if (
      now - writtenAt <= RECENTLY_WRITTEN_TTL_MS &&
      (latest === null || writtenAt > latest)
    ) {
      latest = writtenAt;
    }
  });
  return latest === null ? null : latest + REPLICA_CATCH_UP_MS;
};

/** For tests: nothing carried between cases. */
export const clearRecentlyWrittenCards = () => {
  recentlyWritten.clear();
  notify();
};

/**
 * A card for the dashboard from the Java `Admission` a create or save returns.
 *
 * The response is a superset of the card: AdmissionFull extends the AdmissionPreview view the
 * Java search returned. Only `flagsCount` and `residentFullName` are derived -- the latter the way
 * the replica stores it, so name search and sort agree with the server's rows.
 */
export const workflowCardFromAdmission = (admission: any): WorkflowCard => {
  const answers = admission.answers ?? {};
  const firstName = answers.resident_firstName?.value ?? "";
  const lastName = answers.resident_lastName?.value ?? "";

  return {
    id: admission.id,
    facilityId: admission.facilityId,
    status: admission.status,
    code: admission.code,
    residentFullName: `${firstName} ${lastName}`.trim().toLowerCase(),
    workflowType: admission.workflowType,
    updated: admission.updated,
    created: admission.created,
    dateOfAdmission: admission.dateOfAdmission ?? null,
    packetSize: admission.packetSize ?? null,
    completionPercentage: admission.completionPercentage ?? 0,
    flagsCount: admission.flags?.length ?? 0,
    checklist: admission.checklist ?? [],
    starred: Boolean(admission.starred),
    templateDocumentTypes: admission.templateDocumentTypes ?? [],
    answers,
    resources: admission.resources ?? null,
    signatureDate: admission.signatureDate ?? null,
    facilityTimeZoneId: admission.facilityTimeZoneId ?? null,
    residentId: admission.residentId ?? null,
    outdatedContract: Boolean(admission.outdatedContract),
    sourceWorkflowId: admission.sourceWorkflowId ?? null,
  } as WorkflowCard;
};

type ColumnFilter = Pick<
  Params,
  "status" | "facilityIds" | "workflowType" | "starredOnly"
> & {
  query?: string;
};

/** Whether `card` belongs in a column with these filters -- the server's rules, simply. */
const belongsIn = (card: WorkflowCard, filter: ColumnFilter) => {
  if (filter.status && card.status !== filter.status) {
    return false;
  }
  if (
    filter.facilityIds?.length &&
    !filter.facilityIds.includes(card.facilityId)
  ) {
    return false;
  }
  if (filter.workflowType && card.workflowType !== filter.workflowType) {
    return false;
  }
  if (filter.starredOnly && !card.starred) {
    return false;
  }
  if (filter.query) {
    // A plain substring rather than the service's matching: a miss here only means the card
    // waits for the replica, which is what happened before.
    const term = filter.query.toLowerCase();
    return (
      card.residentFullName.toLowerCase().includes(term) ||
      card.code.toLowerCase().includes(term)
    );
  }
  return true;
};

const mergeRecentlyWritten = (
  data: {pages: SearchResponse[]; pageParams: unknown[]},
  filter: ColumnFilter,
) => {
  if (!recentlyWritten.size || !data.pages.length) {
    return data;
  }

  const now = Date.now();
  let pages = data.pages;
  let delta = 0;

  recentlyWritten.forEach(({card, writtenAt}) => {
    if (now - writtenAt > RECENTLY_WRITTEN_TTL_MS) {
      return;
    }
    const served = pages
      .flatMap(page => page.cards)
      .find(row => row.id === card.id);
    if (served && served.updated >= card.updated) {
      // The replica has caught up; its row is the truth from here.
      return;
    }
    const belongs = belongsIn(card, filter);

    if (served) {
      pages = pages.map(page =>
        page.cards.some(row => row.id === card.id)
          ? {
              ...page,
              cards: belongs
                ? page.cards.map(row => (row.id === card.id ? card : row))
                : page.cards.filter(row => row.id !== card.id),
            }
          : page,
      );
      if (!belongs) {
        delta -= 1;
      }
    } else if (belongs) {
      const [first, ...rest] = pages;
      pages = [{...first, cards: [card, ...first.cards]}, ...rest];
      delta += 1;
    }
  });

  if (pages === data.pages) {
    return data;
  }

  // Every page carries the same total, so the header cannot disagree with the list under it.
  return {
    ...data,
    pages: delta
      ? pages.map(page => ({...page, total: Math.max(0, page.total + delta)}))
      : pages,
  };
};

const unwrap = (response: {ok: boolean; data?: SearchResponse}): SearchResponse => {
  if (!response.ok || !response.data) {
    // Thrown so react-query surfaces it. The apisauce layer has already handled 401/403 by
    // logging out, so anything reaching here is a real failure rather than an expired session.
    throw response;
  }
  return response.data;
};

/**
 * One page of workflows, extended by Load More.
 *
 * An infinite query per column, matching the Java hook it replaces. `refetchQueries` re-fetches
 * every loaded page of one, so a column expanded by Load More stays coherent after an approve
 * or archive — appending to a shared first page would leave the later ones stale.
 */
export const useWorkflowsQuery = (params: Params) => {
  const suppressionRevision = useSuppressionRevision();
  const facilitiesLoaded = params.facilityIds !== null;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  // Normalised before the query key, so "jo" and "" are one cached query.
  const query = normalizeSearchTerm(params.query);
  const facilityKey = params.facilityIds?.join(",");
  /*
   * A fresh closure per revision, not `useCallback(stripSuppressed, ...)`: that hands back the
   * same function object every time, so its identity never changes and react-query never re-runs
   * it. The wrapper is the point.
   *
   * Recently written cards go in before suppression, so a workflow created and then approved at
   * once still leaves its column.
   */
  const select = useCallback(
    (data: {pages: SearchResponse[]; pageParams: unknown[]}) =>
      stripSuppressed(
        mergeRecentlyWritten(data, {
          status: params.status,
          facilityIds: params.facilityIds,
          workflowType: params.workflowType,
          starredOnly: params.starredOnly,
          query,
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the revision is the dependency; facilityKey stands in for the array.
    [
      suppressionRevision,
      params.status,
      facilityKey,
      params.workflowType,
      params.starredOnly,
      query,
    ],
  );

  const result = useInfiniteQuery<SearchResponse>(
    [
      WORKFLOWS_QUERY_KEY,
      {
        status: params.status,
        facilityIds: params.facilityIds,
        query,
        workflowType: params.workflowType,
        starredOnly: params.starredOnly,
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
        pageSize,
      },
    ],
    async ({pageParam = 0}) =>
      unwrap(await WorkflowsService.search(toRequest({...params, query}, pageParam))),
    {
      // From the response, not `total` — that saturates at the service's cap.
      getNextPageParam: ({hasMore}, allPages) => (hasMore ? allPages.length : false),
      enabled:
        facilitiesLoaded &&
        (params.isAllowedToSeeAdmissions === undefined ||
          params.isAllowedToSeeAdmissions),
      /*
       * Off, as it was on the Java hook this replaces, and for a reason that is specific to
       * reading a replica: a checklist tick or a PCC upload lives in this cache because the
       * replica lags the Java write that produced it. A mount-time refetch -- toggling tile and
       * list remounts the column -- would fetch the row as the replica still has it and put the
       * pre-tick card back on screen. The cache is the newer copy here, not the stale one.
       *
       * The paths that genuinely need fresh data (approve, archive, star, delete) refetch this
       * key explicitly, so nothing depends on a mount to notice a change.
       */
      refetchOnMount: false,
      // Applied on the way out rather than to the cache, so no refetch from any path can put a
      // just-moved card back on the board while the replica is still catching up.
      select,
      /*
       * StarFilter disables itself when this is falsy, and nothing else writes it now that the
       * board and the list both read this service. Without it the control is permanently greyed
       * out, whatever the user has starred.
       *
       * The caller's whole count, not this column's: four columns each report the same figure,
       * so the last to land does not shrink it to its own share.
       */
      onSuccess: data => {
        const total = data.pages[data.pages.length - 1]?.totalStarred;
        if (total === undefined) {
          return;
        }
        sessionStorage.setItem("totalStarredAdmissions", total.toString());
        window.dispatchEvent(new Event("starredAdmissionsUpdated"));
      },
    },
  );

  /*
   * Brings in the server's rows for remembered writes. A column fetched before the replica could
   * have a write refetches once it can, and only then, so this cannot undo a write the replica
   * has yet to see. Re-armed by each write (the revision), and settled by the refetch itself:
   * data fetched after the catch-up time needs nothing more. Columns mounted later, such as the
   * board on returning from a pre-flight, run the same check against their cached data.
   */
  const {isSuccess, dataUpdatedAt, refetch} = result;
  useEffect(() => {
    if (!isSuccess) {
      return undefined;
    }
    const dueAt = replicaCatchUpAt(Date.now());
    if (dueAt === null || dataUpdatedAt >= dueAt) {
      return undefined;
    }
    const timer = setTimeout(() => {
      refetch();
    }, Math.max(0, dueAt - Date.now()));
    return () => clearTimeout(timer);
  }, [suppressionRevision, isSuccess, dataUpdatedAt, refetch]);

  /*
   * The term the request actually carried, not the one that was typed. Callers rendering "no
   * matches for X" must use this: below MIN_SEARCH_TERM_LENGTH no term is sent, so the raw
   * prop would blame a search that never ran.
   */
  return {...result, appliedQuery: query};
};
